import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Send, Loader2, Radar, FileBarChart } from 'lucide-react';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

// Admin toggle: pause / resume Telegram notifications (Bot API)
export const TelegramToggle = () => {
    const { getAuthHeaders } = useAuth();
    const [status, setStatus] = useState(null);
    const [busy, setBusy] = useState(false);
    const [detecting, setDetecting] = useState(false);
    const [testing, setTesting] = useState(false);
    const [reporting, setReporting] = useState(false);

    const load = useCallback(async () => {
        try {
            const r = await axios.get(`${API}/api/admin/settings/telegram`, { headers: getAuthHeaders() });
            setStatus(r.data);
        } catch { /* sidebar: fail silently */ }
    }, [getAuthHeaders]);

    useEffect(() => { load(); }, [load]);

    const toggle = async (enabled) => {
        setBusy(true);
        try {
            const r = await axios.put(`${API}/api/admin/settings/telegram`, { enabled }, { headers: getAuthHeaders() });
            setStatus(r.data);
            toast.success(enabled ? 'Notificaciones Telegram activadas' : 'Notificaciones Telegram pausadas');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'No se pudo cambiar');
        } finally {
            setBusy(false);
        }
    };

    const detect = async () => {
        setDetecting(true);
        try {
            const r = await axios.post(`${API}/api/admin/settings/telegram/detect-chat`, {}, { headers: getAuthHeaders() });
            if (r.data.found) {
                toast.success(`Chat detectado: ${r.data.chat_name}`);
                load();
            } else {
                toast.warning(`Aún no hay mensajes. Escribe "hola" a @${r.data.bot_username || 'tu bot'} en Telegram y vuelve a intentar.`);
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || 'No se pudo detectar el chat');
        } finally {
            setDetecting(false);
        }
    };

    const sendTest = async () => {
        setTesting(true);
        try {
            await axios.post(`${API}/api/admin/settings/telegram/test`, {}, { headers: getAuthHeaders() });
            toast.success('Mensaje de prueba enviado a Telegram');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'No se pudo enviar la prueba');
        } finally {
            setTesting(false);
        }
    };

    const sendReport = async () => {
        setReporting(true);
        try {
            const r = await axios.post(`${API}/api/admin/weekly-report/send`, {}, { headers: getAuthHeaders() });
            toast.success(r.data.sent ? `Reporte enviado: ${r.data.count} facturas confirmadas nuevas` : (r.data.reason || 'No se pudo enviar'));
        } catch (e) {
            toast.error(e.response?.data?.detail || 'No se pudo enviar el reporte');
        } finally {
            setReporting(false);
        }
    };

    if (!status) return null;
    const on = status.enabled && !!status.chat_id;
    const statusText = !status.configured ? 'Bot no configurado'
        : !status.chat_id ? 'Sin chat · detecta tu chat'
        : status.enabled ? `Activas · ${status.chat_name || status.chat_id}` : 'Pausadas';

    return (
        <div className="px-4 pb-2" data-testid="telegram-toggle-section">
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${on ? 'bg-sky-100 border-sky-300' : 'bg-slate-100 border-slate-200'}`}>
                <Send className={`w-4 h-4 shrink-0 ${on ? 'text-sky-600' : 'text-slate-400'}`} />
                <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${on ? 'text-sky-800' : 'text-slate-600'}`}>Telegram admin</p>
                    <p className="text-[11px] text-slate-500 truncate" data-testid="telegram-status-text">{statusText}</p>
                </div>
                {busy ? <Loader2 className="w-4 h-4 animate-spin text-slate-400" /> : (
                    <Switch checked={status.enabled} onCheckedChange={toggle} disabled={!status.configured || !status.chat_id} aria-label="Activar o pausar notificaciones Telegram" data-testid="telegram-toggle-switch" />
                )}
            </div>
            {status.configured && (
                <div className="mt-1.5 flex items-center justify-center gap-3 text-[11px]">
                    <button type="button" onClick={detect} disabled={detecting} className="inline-flex items-center gap-1 text-sky-700 hover:text-sky-900 disabled:opacity-50" data-testid="telegram-detect-btn">
                        {detecting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Radar className="w-3 h-3" />} {status.chat_id ? 'Re-detectar chat' : 'Detectar mi chat'}
                    </button>
                    {status.chat_id && (
                        <button type="button" onClick={sendTest} disabled={testing} className="inline-flex items-center gap-1 text-sky-700 hover:text-sky-900 disabled:opacity-50" data-testid="telegram-test-btn">
                            {testing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Enviar prueba
                        </button>
                    )}
                </div>
            )}
            {status.configured && status.chat_id && (
                <div className="mt-1.5 flex items-center justify-center gap-2 text-[11px] text-slate-500" data-testid="weekly-report-row">
                    <FileBarChart className="w-3 h-3 text-sky-600" />
                    <span>Reporte semanal: dom 10 PM</span>
                    <button type="button" onClick={sendReport} disabled={reporting} className="inline-flex items-center gap-1 text-sky-700 hover:text-sky-900 disabled:opacity-50" data-testid="weekly-report-send-btn">
                        {reporting ? <Loader2 className="w-3 h-3 animate-spin" /> : null} Enviar ahora
                    </button>
                </div>
            )}
        </div>
    );
};

export default TelegramToggle;
